// src/oura/components/newscan/Reask.tsx
//
// Edit and rescan, when staff only reworded a few questions (a typo, a wrong
// town): the edited questions are corrected on the finished scan and asked
// again, one at a time, instead of the whole 50 (Jovi, 20 Oct). The backend's
// route is POST /assessments/{id}/questions/{n}/correct (commit 25ad7f07): it
// clears that question's old answers and re-asks only it. The other questions
// keep their answers, the baseline and the question set version stay.
//
// The press is "Re-ask N questions" with a plain confirm. A free dry run runs
// first, quietly: a refusal (the town left out, another question's words)
// shows on that question and nothing is spent. Then each question is sent in
// turn, waiting for the one before (the backend refuses a second while one is
// asking), and the panel shows each one asking, asked or failed.

import { CheckIcon, Loader2Icon, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { Reask } from "@oura/newscan/reask";
import { getBaselineSnapshot, makeBaselineSnapshot } from "@service/aeo/baselineSnapshot";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import { cn } from "@oura/lib/cn";
import { DrawerNotice, PillButton, SURFACE, TYPE } from "@oura/ui";
import { plural } from "@oura/newscan/copy";

const COPY = {
    press: (n: number) => `Re-ask ${plural(n, "question")}`,
    confirmTitle: (n: number) => `Re-ask ${plural(n, "question")}?`,
    confirmBody: (n: number, rest: number) => `The other ${rest} keep their answers. This runs a paid check for ${n === 1 ? "this question" : `these ${n}`}.`,
    cancel: "Cancel",
    checking: "Checking the new wording",
    refused: "Fix these before re-asking. Nothing was spent.",
    running: (done: number, n: number) => `Re-asking ${done} of ${n}`,
    done: (n: number) => `${plural(n, "question")} re-asked. Each shows Edited on the answers.`,
    someFailed: "Some did not go through. Try them again.",
    back: "Back to the answers",
    retry: "Try again",
    snapshotStale: "The Baseline Snapshot was made before these answers, and does not update by itself.",
    remake: "Re-make Snapshot",
    remaking: "Oura is re-making the Baseline Snapshot. It takes a few minutes.",
    remakeFailed: "The Snapshot was not re-made. Try again.",
    snapshotChecking: "Checking whether the Baseline Snapshot needs re-making.",
    snapshotLookupFailed: "We couldn't check whether the Baseline Snapshot is now out of date.",
    checkAgain: "Check again",
    state: { waiting: "Waiting", asking: "Asking the engines", asked: "Asked", failed: "Did not go through" } as const,
} as const;

/** The confirm: a plain question, the other questions' count, and the press. */
export function ReaskConfirm({ reask, count, total }: { reask: Reask; count: number; total: number }) {
    return (
        <Dialog open={reask.confirming} onOpenChange={(open) => reask.setConfirming(open)}>
            <DialogContent className="gap-0 p-6 sm:max-w-[440px] sm:rounded-2xl">
                <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">{COPY.confirmTitle(count)}</DialogTitle>
                <DialogDescription className={cn(TYPE.tb, "mt-2 text-gray-600")}>{COPY.confirmBody(count, total - count)}</DialogDescription>
                {/* Secondary left, the primary last on the right. */}
                <div className="mt-5 flex justify-end gap-2">
                    <PillButton size="sm" onClick={() => reask.setConfirming(false)}>
                        {COPY.cancel}
                    </PillButton>
                    <PillButton size="sm" variant="primary" icon={RefreshCwIcon} writes onClick={() => void reask.run()}>
                        {COPY.press(count)}
                    </PillButton>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** What is happening to each question once the press is made. Nothing before it. */
export function ReaskProgress({ reask, onBack, assessmentId }: { reask: Reask; onBack(): void; assessmentId: string | null }) {
    // Ipul's handoff: a correction recomputes the scores, but the Baseline Snapshot stays as it was.
    // A failed check is said, with Check again: read as "no snapshot" it let staff leave a stale one (Jack, #4468).
    const [snapshot, setSnapshot] = useState<"none" | "checking" | "stale" | "making" | "failed" | "lookup_failed">("none");
    const anyAsked = reask.rows.some((r) => r.state === "asked");
    const checkSnapshot = useCallback(async () => {
        if (!assessmentId) return;
        setSnapshot("checking");
        try {
            const r = await getBaselineSnapshot(assessmentId);
            if (!r.ok) {
                setSnapshot("lookup_failed");
                return;
            }
            setSnapshot(r.read.status === "ready" ? "stale" : "none");
        } catch {
            setSnapshot("lookup_failed");
        }
    }, [assessmentId]);
    useEffect(() => {
        if (reask.phase !== "done" || !anyAsked || !assessmentId) return;
        void checkSnapshot();
    }, [reask.phase, anyAsked, assessmentId, checkSnapshot]);
    const remake = async () => {
        if (!assessmentId) return;
        setSnapshot("making");
        try {
            const r = await makeBaselineSnapshot(assessmentId);
            if (!r.ok) setSnapshot("failed");
        } catch {
            setSnapshot("failed");
        }
    };
    // Not while the check runs. After a failed check, leaving sits beside Check again, never alone,
    // so a check that keeps failing cannot strand staff here.
    const snapshotUnsettled = snapshot === "checking";
    if (reask.phase === "idle") return null;
    const n = reask.rows.length;
    const asked = reask.rows.filter((r) => r.state === "asked").length;
    const failed = reask.rows.some((r) => r.state === "failed");
    const line =
        reask.phase === "checking"
            ? COPY.checking
            : reask.phase === "refused"
              ? COPY.refused
              : reask.phase === "running"
                ? COPY.running(Math.min(asked + 1, n), n)
                : failed
                  ? COPY.someFailed
                  : COPY.done(n);
    const box = (
        <ul role="list" className="list-plain m-0 mt-2 space-y-1.5 p-0">
            {reask.rows.map((r) => (
                <li key={r.q.n} className={cn(TYPE.tc, "flex items-start gap-2")}>
                    <span aria-hidden className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center">
                        {r.state === "asked" ? (
                            <CheckIcon size={14} strokeWidth={2.5} className="text-emerald-600" />
                        ) : r.state === "asking" ? (
                            <Loader2Icon size={14} className="text-blue-600 motion-safe:animate-spin" />
                        ) : (
                            <span className={cn("h-1.5 w-1.5 rounded-full", r.state === "failed" ? "bg-rose-500" : "bg-gray-300")} />
                        )}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="text-gray-900">
                            <span className="tabular-nums text-gray-400">{r.q.n}.</span> {r.q.text}
                        </span>
                        <span className={cn("block", r.error ? "text-rose-600" : "text-gray-500")}>{r.error ?? COPY.state[r.state]}</span>
                    </span>
                </li>
            ))}
        </ul>
    );
    if (reask.phase === "refused") return <DrawerNotice>{COPY.refused}{box}</DrawerNotice>;
    return (
        <section aria-live="polite" className={cn(SURFACE.block, "space-y-1")}>
            <p className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{line}</p>
            {box}
            {reask.phase === "done" && snapshot !== "none" && (
                <p className={cn(TYPE.tc, "m-0 pt-2", snapshot === "failed" || snapshot === "lookup_failed" ? "text-rose-600" : "text-gray-600")}>
                    {snapshot === "making"
                        ? COPY.remaking
                        : snapshot === "failed"
                          ? COPY.remakeFailed
                          : snapshot === "checking"
                            ? COPY.snapshotChecking
                            : snapshot === "lookup_failed"
                              ? COPY.snapshotLookupFailed
                              : COPY.snapshotStale}
                </p>
            )}
            {reask.phase === "done" && (
                <div className="flex flex-wrap justify-end gap-2 pt-2">
                    {snapshot === "lookup_failed" && (
                        <PillButton size="sm" writes onClick={() => void checkSnapshot()}>
                            {COPY.checkAgain}
                        </PillButton>
                    )}
                    {(snapshot === "stale" || snapshot === "failed") && (
                        <PillButton size="sm" writes icon={RefreshCwIcon} onClick={() => void remake()}>
                            {COPY.remake}
                        </PillButton>
                    )}
                    {failed && (
                        <PillButton size="sm" writes onClick={() => void reask.run()}>
                            {COPY.retry}
                        </PillButton>
                    )}
                    {!snapshotUnsettled && (
                        <PillButton size="sm" variant="primary" onClick={onBack}>
                            {COPY.back}
                        </PillButton>
                    )}
                </div>
            )}
        </section>
    );
}
